// /stats: devices by day, attainment per task, and time per unit from the
// logs beside the register's figure, with a suggested session size.
import type { StatsView } from "../lib/types";
import { TaskChip } from "../design";
import { useApi } from "../api/live";
import { paths } from "../api/paths";
import { dayMonth, weekday } from "../lib/days";
import { useTasks } from "../lib/useRegister";
import { Page } from "../shell/Page";
import { ErrorNote, Loading } from "../shell/ui";
import { safeStats } from "../lib/safe";

export function StatsPage() {
  const stats = useApi<StatsView>(paths.stats(), ["session"]);
  const { byCode } = useTasks();
  const s = stats.data ? safeStats(stats.data) : null;
  if (!s) return <Page title="Stats">{stats.error ? <ErrorNote error={stats.error} retry={stats.reload} /> : <Loading />}</Page>;
  const top = Math.max(10, ...s.devicesByDay.map((d) => d.devices));
  const total = s.devicesByDay.reduce((n, d) => n + d.devices, 0);
  const tone = (p: number) => (p >= 0.9 ? "" : p >= 0.7 ? " meter--a" : " meter--r");
  return (
    <Page title="Stats">
      <div className="stack">
        <section className="card">
          <div className="card__head"><h2>Devices finished</h2><span className="small muted">{dayMonth(s.from)} to {dayMonth(s.to)}: {total} in all</span></div>
          <div className="bars" role="img" aria-label={`Devices finished per day, ${total} in all`}>
            {s.devicesByDay.map((d) => (
              <div key={d.day} className="bars__b" title={`${weekday(d.day)} ${dayMonth(d.day)}: ${d.devices}`}>
                <span className="bars__n">{d.devices || ""}</span>
                <span className="bars__fill" style={{ height: `${(d.devices / top) * 100}%` }} />
                <span>{weekday(d.day).slice(0, 1)}</span>
              </div>
            ))}
          </div>
        </section>
        <div className="grid-2">
          <section className="card scroll-x">
            <div className="card__head"><h2>Made against planned</h2><span className="small muted">From the session log</span></div>
            <table className="table">
              <thead><tr><th>Task</th><th className="num">Planned</th><th className="num">Made</th><th>Share</th><th className="num">Sessions</th></tr></thead>
              <tbody>{[...s.attainment].sort((a, b) => b.sessions - a.sessions).map((a) => {
                const p = a.planned ? a.made / a.planned : 0;
                return (
                  <tr key={a.task}><td><TaskChip task={a.task} name={byCode[a.task]?.name} group={byCode[a.task]?.group} /></td>
                    <td className="num">{a.planned}</td><td className="num">{a.made}</td>
                    <td><div className={`meter${tone(p)}`}><span style={{ width: `${Math.min(100, p * 100)}%` }} /></div><span className="small muted">{Math.round(p * 100)}%</span></td>
                    <td className="num">{a.sessions}</td></tr>
                );
              })}</tbody>
            </table>
          </section>
          <section className="card scroll-x">
            <div className="card__head"><h2>Time per unit</h2><span className="small muted">Flexible time is left out</span></div>
            <table className="table">
              <thead><tr><th>Task</th><th className="num">Register</th><th className="num">Logged</th><th className="num">Suggested session</th></tr></thead>
              <tbody>{s.rates.map((r) => {
                const now = byCode[r.task]?.sessionQty;
                return (
                  <tr key={r.task}><td><TaskChip task={r.task} name={byCode[r.task]?.name} group={byCode[r.task]?.group} /></td>
                    <td className="num">{r.registerMinutes != null ? `${r.registerMinutes} min` : "—"}</td>
                    <td className="num">{r.loggedMinutes != null ? `${r.loggedMinutes} min` : <span className="muted" title="Not enough logged sessions with a start and finish">—</span>}</td>
                    <td className="num">{r.suggestedSessionQty != null && now != null && r.suggestedSessionQty !== now
                      ? <span className="pill pill--a">{now} → {r.suggestedSessionQty}</span> : <span className="muted">{now ?? "—"}</span>}</td></tr>
                );
              })}</tbody>
            </table>
            <p className="small muted" style={{ marginTop: "var(--space-3)" }}>A suggestion, not a change: session sizes are set in Settings.</p>
          </section>
        </div>
      </div>
    </Page>
  );
}
